import { Grow } from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import SearchIcon from "@material-ui/icons/Search";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
import Fuse from "fuse.js";
import { AllHtmlEntities } from "html-entities";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Gallery from "react-grid-gallery";
import Lightbox, { ModalGateway, Modal as Wrapper } from "react-images";
import { useMediaQuery } from "react-responsive";
import CustomContainer from "./CustomContainer";
import CustomWrapper from "./CustomWrapper";
import ThumbnailImageComponent from "./ThumbnailImageComponent";
import "./grid.css";

export const useSelfClearingTimeout = (callback, timeout) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      callback();
    }, timeout);
    return () => {
      clearTimeout(timer);
    };
  }, [callback, timeout]);
};

const Carousel = ({ categorisedMedia }) => {
  const [showingModal, setShowingModal] = useState();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const [autoCompleteInput, setAutoCompleteInput] = useState("");
  const firstRender = useRef(true);
  const [triggerRerender, setTriggerRerender] = useState(false);

  useSelfClearingTimeout(() => {
    if (firstRender.current) {
      firstRender.current = false;
    }

    if (!triggerRerender) {
      setTriggerRerender(true);
    }
  }, 20);

  const showModal = async (index) => {
    await setShowingModal(index);
    disableBodyScroll(document.querySelector(".mobile-image-view"));
  };

  const closeModal = () => {
    setShowingModal();
    clearAllBodyScrollLocks();
    const url = new URL(window.location);
    url.searchParams.delete("id");
    window.history.pushState({}, "", url);
  };

  const customStyles = {
    positioner: (base) => ({
      ...base,
      bottom: `${isTabletOrMobile ? "inherit" : base.bottom}`,
    }),
    blanket: (base) => ({
      ...base,
    }),
  };

  const customGalleryStyles = {
    header: (base) => ({
      ...base,
      opacity: "1",
    }),
  };

  const images = [];
  categorisedMedia.forEach(
    ({
      node: {
        caption,
        source_url,
        description,
        media_details,
        title,
        wordpress_id,
      },
    }) => {
      const htmlEntity = new AllHtmlEntities();
      const hoverText = htmlEntity.decode(caption.replace(/(<([^>]+)>)/gi, ""));
      const sanitisedDescription = htmlEntity.decode(
        description.replace(/(<([^>]+)>)/gi, "")
      );
      const sanitisedTitle = htmlEntity.decode(
        title.replace(/(<([^>]+)>)/gi, "")
      );
      images.push({
        src: source_url,
        hoverCaption: hoverText,
        sanitisedDescription,
        sanitisedTitle,
        description,
        thumbnail: source_url,
        thumbnailWidth: media_details.width,
        width: media_details.width,
        height: media_details.height,
        title,
        id: wordpress_id,
      });
    }
  );

  const options = {
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.2,
    keys: ["sanitisedDescription", "sanitisedTitle", "hoverCaption"],
  };

  const fuse = new Fuse(images, options);

  const rawFilterOptions = fuse.search(autoCompleteInput).map((result) => ({
    ...result,
    item: {
      ...result.item,
      isFilteredOption: true,
    },
  }));
  const exactMatches = rawFilterOptions.filter(
    ({ item }) => item.sanitisedTitle === autoCompleteInput
  );
  const filterOptions = autoCompleteInput
    ? exactMatches.length > 0
      ? exactMatches.map(({ item }) => item)
      : rawFilterOptions.map(({ item }) => item)
    : images;

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const photoId = queryParams.get("id");
    const photoLookup = filterOptions.find(({ id }) => Number(photoId) === id);
    const photoIndex = filterOptions.indexOf(photoLookup);
    if (photoLookup && photoIndex !== -1) {
      showModal(photoIndex);
    }
  }, []);

  const setQueryParams = (index, images) => {
    const url = new URL(window.location);
    const imageId = filterOptions[index].id;
    url.searchParams.set("id", imageId);
    window.history.pushState({}, "", url);
  };

  return (
    <div>
      {!firstRender.current && images.length > 0 && (
        <Grow in timeout={750} direction="down">
          <Autocomplete
            freeSolo
            onInputChange={(_, value) => setAutoCompleteInput(value)}
            options={filterOptions.map(({ sanitisedTitle }) => sanitisedTitle)}
            style={{
              width: isTabletOrMobile ? "100%" : "50%",
              marginBottom: "10px",
              marginTop: "24px",
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                style={{ ...params.style }}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    ...params.InputProps.style,
                    paddingRight: "30px",
                  },
                  startAdornment: (
                    <InputAdornment
                      style={{ width: "18px", paddingLeft: "4px" }}
                      position="start"
                    >
                      <SearchIcon
                        style={{
                          width: "22px",
                          height: "22px",
                          color: "#cacccc",
                        }}
                      />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                }}
                label="Search exhibits on this page..."
                variant="outlined"
              />
            )}
            filterOptions={(x) => x}
          />
        </Grow>
      )}
      <Gallery
        rowHeight={240}
        images={autoCompleteInput ? filterOptions : images}
        enableImageSelection={false}
        margin={15}
        thumbnailImageComponent={ThumbnailImageComponent}
        onClickThumbnail={(index) => {
          showModal(index);
          setQueryParams(index, images);
        }}
      />
      {showingModal + 1 > 0 && (
        <ModalGateway>
          <Wrapper
            allowFullscreen
            onClose={() => closeModal()}
            styles={customStyles}
          >
            <Lightbox
              currentIndex={showingModal}
              views={filterOptions}
              isFullscreen
              styles={customGalleryStyles}
              trackProps={{
                onViewChange: (index) => setQueryParams(index, filterOptions),
              }}
              components={{
                View: CustomContainer,
                Container: CustomWrapper,
                Footer: null,
              }}
            />
          </Wrapper>
        </ModalGateway>
      )}
    </div>
  );
};

Carousel.propTypes = {
  categorisedMedia: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Carousel;
